/**
 * User Service
 * Handles all user-related API operations
 */

import api from '@services/api';
import type { User } from '@/types/models';

class UserService {
  /**
   * Get users for sharing (simplified list for sharing UI)
   */
  async getUsersForSharing(search?: string, limit?: number): Promise<User[]> {
    const params = new URLSearchParams();

    // Sanitize search input
    if (search) {
      const sanitized = search.trim().slice(0, 100); // Max 100 chars
      // Remove any potentially dangerous characters
      const cleaned = sanitized.replace(/[<>'"]/g, '');
      if (cleaned) params.append('search', cleaned);
    }

    // Validate limit
    if (limit && limit > 0 && limit <= 1000) {
      params.append('limit', limit.toString());
    }

    const queryString = params.toString();
    const endpoint = `/users/for-sharing${queryString ? `?${queryString}` : ''}`;

    const { data } = await api.get(endpoint);
    return data.users || [];
  }

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<User> {
    const { data } = await api.get('/users/me');
    return data.user;
  }

  /**
   * Update current user profile
   */
  async updateCurrentUser(payload: Partial<User>): Promise<User> {
    const { data } = await api.put('/users/me', payload);
    return data.user;
  }

  /**
   * List all users (admin only)
   */
  async listUsers(page: number = 1, perPage: number = 20, roleFilter: string = 'all'): Promise<{
    users: User[];
    pagination: any;
  }> {
    const { data } = await api.get(`/users?page=${page}&per_page=${perPage}&role=${roleFilter}`);
    return {
      users: data.users || [],
      pagination: data.pagination
    };
  }

  /**
   * Invite a new user (admin only)
   */
  async inviteUser(email: string, role: string = 'user'): Promise<any> {
    const { data } = await api.post('/users/invite', { email, role });
    return data;
  }

  /**
   * List pending invitations (admin only)
   */
  async listInvitations(): Promise<any[]> {
    const { data } = await api.get('/users/invitations');
    return data.invitations || [];
  }
}

// Create and export singleton instance
const userService = new UserService();

export default userService;
